import { ReactNode } from 'react'
import { RiaSourceTriggerLocation } from '@/analytics/enums/ria.enums'
import { CitatorSurface } from '@/analytics/enums/citator.enums'
import { kUnverifiedSourceLabel } from '@/agent/references/constants'
import { useCitationSourceTooltip } from '@/agent/references/hooks/useCitationSourceTooltip'
import CitationSourceTooltip from './CitationSourceTooltip'

type LegalCitationLinkProps = {
  citation_id: string
  citation_id_index: number
  /** Footnote label ([1], [1.2]) shown in the tooltip so the legal citation link maps to the sources list. */
  citationLabel?: string
  /** The authority text, e.g. "Osborn v. Irwin Memorial Blood Bank, 5 Cal. App. 4th 234 (1992)". */
  children: ReactNode
}

/**
 * Inline clickable authority citation: the authority string itself is the click target,
 * rendered where the authority sits in the prose. Hover (~1s) shows the source tooltip
 * with metadata and the citator case-status treatment; clicking opens the content
 * viewer at this occurrence's highlights.
 *
 * When the reference isn't (yet) resolvable — mid-stream before the reference payload
 * arrives, or a citation id the turn doesn't know — the authority text renders visibly
 * distinct (grey, "Not linked" tooltip) and non-clickable. It must never be
 * dropped: unlike a badge, the legal citation link IS part of the sentence. (The
 * canvas renders the same greyed-out state via the legalCitation mark.)
 */
export default function LegalCitationLink(props: LegalCitationLinkProps) {
  const { citation_id, citation_id_index, citationLabel, children } = props

  const { reference, isActive, caseStatus, isOpening, error, handleTooltipOpen, openCitation, handleViewCitationAnalysis } = useCitationSourceTooltip({
    citation_id,
    citation_id_index,
    triggerLocation: RiaSourceTriggerLocation.InlineReference,
    surface: CitatorSurface.Chat,
  })

  if (!reference) {
    return (
      <span title={kUnverifiedSourceLabel} className="text-brand-neutral-400 underline decoration-brand-neutral-400/40 underline-offset-2">
        {children}
      </span>
    )
  }

  return (
    <CitationSourceTooltip
      reference={reference}
      citationLabel={citationLabel}
      caseStatus={caseStatus}
      isOpening={isOpening}
      error={error}
      onOpen={handleTooltipOpen}
      onViewReference={openCitation}
      onViewCitationAnalysis={() => void handleViewCitationAnalysis()}
    >
      <button
        onClick={openCitation}
        className={`inline text-left text-brand-700 underline decoration-brand-700/40 underline-offset-2 hover:text-brand-800 hover:decoration-brand-800 ${
          isActive ? ' bg-brand-neutral-200 rounded-md' : ''
        }`}
      >
        {children}
      </button>
    </CitationSourceTooltip>
  )
}
